import { buttonStyle } from 'components/buttons/buttonStyle';
import NumberInput from 'components/forms/inputs/rhf-inputs/NumberInput.tsx';
import InfoTooltip from 'components/InfoTooltip';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useUpdateRankingsRefereeRestPeriodMutation } from 'store/api/tenants.ts';
import { RankingsRefereeRestPeriodUpdateDto, TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';

const UPDATE_PERMISSIONS = ['UPDATE.BACK_OFFICE_SETTINGS'];

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

/** How many days after a request a BD effort warns before the same person is asked to be a referee again. */
const RankingsRefereeRestPeriodControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updateRestPeriod] = useUpdateRankingsRefereeRestPeriodMutation();
    const hasUpdatePermission = useHasPermissions(UPDATE_PERMISSIONS);

    const { handleSubmit, control } = useForm<RankingsRefereeRestPeriodUpdateDto>({
        defaultValues: {
            rankingsRefereeRestPeriodDays: tenantConfiguration.rankingsRefereeRestPeriodDays,
        },
    });

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.rankings-referee.header'),
                id: 'rankings-referee-rest-period',
                enableSorting: false,
                cell: () => (
                    <div className="new:grid-cols-[16.25rem_auto_1fr] grid grid-cols-[19.75rem_auto_1fr] items-center gap-x-10 gap-y-2">
                        <p className="text-body2 flex items-center gap-1">
                            <FormattedMessage id="configuration.rankings-referee.rest-period" />
                            <InfoTooltip text="configuration.rankings-referee.rest-period-tooltip" />
                        </p>
                        <NumberInput
                            control={control}
                            name="rankingsRefereeRestPeriodDays"
                            inputProps={{
                                min: 0,
                                max: 3650,
                                'aria-label': t('configuration.rankings-referee.rest-period'),
                            }}
                            required
                            disabled={!hasUpdatePermission}
                            wrapperClassName="w-24"
                        />
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-8 justify-self-start')}
                            onClick={() => {
                                void handleSubmit((data) =>
                                    updateRestPeriod({
                                        rankingsRefereeRestPeriodDays: Number(data.rankingsRefereeRestPeriodDays),
                                    })
                                )();
                            }}
                            disabled={!hasUpdatePermission}
                        >
                            <FormattedMessage id="save" />
                        </Button>
                    </div>
                ),
            },
        ],
        [control, handleSubmit, hasUpdatePermission, updateRestPeriod]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.rankings-referee.title" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-rankings-referee-rest-period"
            />
        </>
    );
};

export default RankingsRefereeRestPeriodControl;
